import { formatTopPercentDisplay } from "./formatters.js";

export const MARKETPLACE_CSV_COLUMNS = [
  { header: "Rank", value: ({ rank }) => rank },
  { header: "1d Delta", value: ({ change }) => formatCsvChange(change) },
  { header: "Top %", value: ({ rank, topPercentTotal }) => formatCsvTopPercent(rank, topPercentTotal) },
  { header: "Name", value: ({ item }) => item.name || "" },
  { header: "URL", value: ({ item }) => item.url || "" },
  { header: "Price", value: ({ item }) => item.price || "" },
  { header: "Creator", value: ({ item }) => item.authorName || "" },
  { header: "Creator URL", value: ({ item }) => item.authorUrl || "" },
  {
    header: "Categories",
    value: ({ item, categoryMetadataByType }) => formatCsvCategories(item, categoryMetadataByType),
  },
  {
    header: "Medals - Last Week - Free/Paid",
    value: ({ item, categoryMetadataByType }) => formatCsvMedals(item, categoryMetadataByType),
  },
  { header: "Type", value: ({ item, activeType }) => item.type || activeType },
  { header: "Context", value: ({ context }) => context },
];

export const CREATOR_CSV_COLUMNS = [
  { header: "Name", value: ({ item }) => item.name || item.creatorName || "" },
  { header: "Creator URL", value: ({ item }) => item.creatorUrl || item.url || "" },
  { header: "Templates", value: ({ item }) => getCreatorStatValue(item, "totalTemplates") },
  { header: "Templates 1d Delta", value: ({ item }) => getCreatorStatValue(item, "totalTemplatesDelta1d") },
  { header: "Components", value: ({ item }) => getCreatorStatValue(item, "totalComponents") },
  { header: "Components 1d Delta", value: ({ item }) => getCreatorStatValue(item, "totalComponentsDelta1d") },
  { header: "Plugins", value: ({ item }) => getCreatorStatValue(item, "totalPlugins") },
  { header: "Plugins 1d Delta", value: ({ item }) => getCreatorStatValue(item, "totalPluginsDelta1d") },
  { header: "Vectors", value: ({ item }) => getCreatorStatValue(item, "totalVectors") },
  { header: "Vectors 1d Delta", value: ({ item }) => getCreatorStatValue(item, "totalVectorsDelta1d") },
  { header: "Tutorials", value: ({ item }) => getCreatorStatValue(item, "totalTutorials") },
  { header: "Tutorials 1d Delta", value: ({ item }) => getCreatorStatValue(item, "totalTutorialsDelta1d") },
  { header: "Total", value: ({ item }) => getCreatorStatValue(item, "totalItems") },
  { header: "Total 1d Delta", value: ({ item }) => getCreatorStatValue(item, "totalItemsDelta1d") },
];

export function escapeCsvCell(value) {
  const text = value === null || value === undefined ? "" : String(value).replace(/[\r\n]+/g, " ");
  return `"${text.replace(/"/g, '""')}"`;
}

export function sanitizeCsvFilenamePart(value) {
  const normalized = String(value ?? "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return normalized || "none";
}

export function getCsvDataDate(lastUpdated, now = new Date()) {
  const value = lastUpdated || now.toISOString();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return now.toISOString().slice(0, 10);
  return parsed.toISOString().slice(0, 10);
}

export function buildCsvFilename({ dataLastUpdated, type, tab, category, sortColumn, sortDirection, search, now }) {
  const categoryPart = category ? category : "all-categories";
  const searchPart = search ? sanitizeCsvFilenamePart(search) : "none";
  const parts = [
    "framer-ranks",
    getCsvDataDate(dataLastUpdated, now),
    type,
    tab,
    categoryPart,
    `sort-${sortColumn}-${sortDirection}`,
    `search-${searchPart}`,
  ].map(sanitizeCsvFilenamePart);
  return `${parts.join("_")}.csv`;
}

export function formatCsvChange(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") return String(value);
  return String(value);
}

export function formatCsvTopPercent(rank, total) {
  return formatTopPercentDisplay(rank, total);
}

function getCategoryMetaForType(type, categoryMetadataByType = {}) {
  if (type === "component") return categoryMetadataByType.component || {};
  if (type === "plugin") return categoryMetadataByType.plugin || {};
  if (type === "vector") return categoryMetadataByType.vector || {};
  if (type === "tutorial") return categoryMetadataByType.tutorial || {};
  return categoryMetadataByType.template || categoryMetadataByType.category || {};
}

export function normalizeCategoryDisplayName(name) {
  if (typeof name !== "string") return name;
  const normalized = name
    .replace(/\s*Templates?\s*$/i, "")
    .replace(/\s*Components?\s*$/i, "")
    .replace(/\bCms\b/g, "CMS")
    .replace(/\bAi\b/g, "AI")
    .replace(/\bUi\b/g, "UI")
    .replace(/\bSeo\b/g, "SEO")
    .trim();
  return normalized.length > 0 ? normalized : name.trim();
}

function getCsvCategoryLabel(categoryId, itemType, categoryMetadataByType = {}) {
  const meta = getCategoryMetaForType(itemType, categoryMetadataByType);
  return normalizeCategoryDisplayName(meta?.[categoryId]?.name || String(categoryId || ""));
}

export function formatCsvCategories(item, categoryMetadataByType = {}) {
  const categories = Array.isArray(item?.categories) ? item.categories : [];
  return categories
    .map((categoryId) => getCsvCategoryLabel(categoryId, item?.type || "template", categoryMetadataByType))
    .filter(Boolean)
    .join(", ");
}

export function formatCsvMedals(item, categoryMetadataByType = {}) {
  const medals = Array.isArray(item?.badges?.medals) ? item.badges.medals : [];
  if (medals.length === 0) return "";

  const categoriesByRank = new Map([
    [1, []],
    [2, []],
    [3, []],
  ]);
  for (const medal of medals) {
    const rank = Number(medal?.rank);
    if (rank !== 1 && rank !== 2 && rank !== 3) continue;
    const categoryId = medal?.categoryId || medal?.category;
    const categoryName = getCsvCategoryLabel(categoryId, item?.type || "template", categoryMetadataByType);
    if (!categoryName) continue;
    const names = categoriesByRank.get(rank);
    if (!names.includes(categoryName)) names.push(categoryName);
  }

  return [1, 2, 3]
    .map((rank) => {
      const names = categoriesByRank.get(rank) || [];
      if (names.length === 0) return "";
      return `#${rank} in ${names.join(", ")}`;
    })
    .filter(Boolean)
    .join("; ");
}

function getCreatorStats(item) {
  return item?.creatorStats || item || {};
}

function getCreatorStatValue(item, key) {
  const stats = getCreatorStats(item);
  const value = stats?.[key];
  if (value === null || value === undefined) return "";
  return value;
}

function getRankEntry(item, context) {
  const entry = item?.ranks?.[context];
  return Array.isArray(entry) ? entry : null;
}

export function buildCsvRows({
  type,
  rows,
  resolveItem = (row) => row?.item || row,
  context,
  categoryMetadataByType = {},
  topPercentTotal = 0,
}) {
  const columns = type === "creator" ? CREATOR_CSV_COLUMNS : MARKETPLACE_CSV_COLUMNS;
  const headers = columns.map((column) => column.header);
  const dataRows = (Array.isArray(rows) ? rows : []).map((row) => {
    const item = resolveItem(row) || {};
    const rankEntry = type === "creator" ? null : getRankEntry(item, context);
    const rank = rankEntry?.[0] ?? "";
    const change = rankEntry?.[1] ?? "";
    const descriptorInput = {
      row,
      item,
      rank,
      change,
      activeType: type,
      context,
      categoryMetadataByType,
      topPercentTotal,
    };
    return columns.map((column) => column.value(descriptorInput));
  });
  return [headers, ...dataRows];
}

export function stringifyCsvRows(rows) {
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

export function buildCsv(input) {
  return stringifyCsvRows(buildCsvRows(input));
}
